// Voice input for the AI Agent composer — HYBRID.
//
// Both engines run together on every recording:
//  • MediaRecorder ALWAYS records the clip and sends it to the backend (Groq
//    Whisper + PCU domain prompt) — this is the authoritative transcript, and the
//    only one that gets Palestinian place names right (نابلس, رام الله …).
//  • SpeechRecognition runs in parallel purely for instant on-screen feedback,
//    so text appears while the admin is still speaking.
//
// The live text is provisional: when Whisper returns it REPLACES the live text
// rather than appending, so the admin ends up with the accurate version and
// never sees duplicated words.
//
// Everything is observable (status + level + lastError) because a voice UI that
// fails silently is impossible to debug from the outside.
import { useCallback, useEffect, useRef, useState } from 'react';
import { transcribeAudio } from './agentApi';

// Pick a container the browser can record AND the STT provider accepts.
// Whisper accepts webm/ogg/mp4/m4a/wav — Chrome gives webm/opus, Safari mp4.
const pickMimeType = (): string => {
  const MR = (window as any).MediaRecorder;
  if (!MR?.isTypeSupported) return '';
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
  return candidates.find((c) => MR.isTypeSupported(c)) || '';
};

const getSpeechRecognition = (): any =>
  typeof window === 'undefined'
    ? null
    : (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition || null;

const canRecord = (): boolean =>
  typeof window !== 'undefined' &&
  !!navigator.mediaDevices?.getUserMedia &&
  typeof (window as any).MediaRecorder !== 'undefined';

export const isVoiceSupported = (): boolean =>
  typeof window !== 'undefined' && (canRecord() || !!getSpeechRecognition());

const MIN_MS = 400;      // shorter than this is an accidental tap
const MAX_MS = 120_000;  // hard stop so a forgotten recording can't run away

/** What the UI should be showing right now. */
export type VoiceStatus = 'idle' | 'starting' | 'listening' | 'transcribing' | 'error';

/** Which engine produced the text currently in the composer. */
export type VoiceEngine = 'live' | 'whisper' | null;

interface Options {
  // Definitive text for this recording session. Called repeatedly — the caller
  // must REPLACE the session's previous text, not append.
  onResult: (text: string, engine: Exclude<VoiceEngine, null>) => void;
  // Provisional words still being spoken (preview only, not committed).
  onInterim?: (text: string) => void;
  onError?: (message: string) => void;
  language?: string;
}

export const useVoiceInput = ({ onResult, onInterim, onError, language = 'ar' }: Options) => {
  const [status, setStatus] = useState<VoiceStatus>('idle');
  const [seconds, setSeconds] = useState(0);
  const [level, setLevel] = useState(0);          // 0..1 mic input level
  const [engine, setEngine] = useState<VoiceEngine>(null);
  const [liveActive, setLiveActive] = useState(false);   // SpeechRecognition running?
  const [lastError, setLastError] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const recognitionRef = useRef<any>(null);
  const startedAtRef = useRef(0);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const maxTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const stopRef = useRef<() => void>(() => {});
  // Text the live engine has finalized so far this session.
  const liveTextRef = useRef('');
  // Audio-level plumbing.
  const audioCtxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);

  const cbRef = useRef({ onResult, onInterim, onError });
  cbRef.current = { onResult, onInterim, onError };

  const fail = (msg: string) => {
    setLastError(msg);
    setStatus('error');
    cbRef.current.onError?.(msg);
  };

  const cleanupTimers = () => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (maxTimerRef.current) { clearTimeout(maxTimerRef.current); maxTimerRef.current = null; }
  };

  const stopMeter = () => {
    if (rafRef.current !== null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    audioCtxRef.current?.close().catch(() => {});
    audioCtxRef.current = null;
    setLevel(0);
  };

  // Drive a simple RMS level so the user can SEE the mic is picking them up,
  // even before any words come back.
  const startMeter = (stream: MediaStream) => {
    try {
      const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) return;
      const ctx: AudioContext = new Ctx();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const loop = () => {
        analyser.getByteFrequencyData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
        const rms = Math.sqrt(sum / buf.length) / 255;
        setLevel(Math.min(1, rms * 2.2)); // scale so normal speech reads high
        rafRef.current = requestAnimationFrame(loop);
      };
      rafRef.current = requestAnimationFrame(loop);
    } catch { /* level meter is cosmetic — never block recording */ }
  };

  const startTicker = () => {
    startedAtRef.current = Date.now();
    setSeconds(0);
    tickRef.current = setInterval(
      () => setSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000)),
      250,
    );
    maxTimerRef.current = setTimeout(() => stopRef.current(), MAX_MS);
  };

  // ── Live preview (best effort; never fatal) ───────────────────────────────
  const attachLive = useCallback(() => {
    const SR = getSpeechRecognition();
    if (!SR) return;
    try {
      const rec = new SR();
      rec.lang = language === 'ar' ? 'ar-SA' : language === 'en' ? 'en-US' : language;
      rec.continuous = true;
      rec.interimResults = true;

      rec.onresult = (event: any) => {
        let interim = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const r = event.results[i];
          const txt = String(r[0]?.transcript || '');
          if (r.isFinal) {
            liveTextRef.current = `${liveTextRef.current} ${txt.trim()}`.trim();
            if (liveTextRef.current) {
              setEngine('live');
              cbRef.current.onResult(liveTextRef.current, 'live');
            }
            cbRef.current.onInterim?.('');
          } else {
            interim += txt;
          }
        }
        if (interim.trim()) cbRef.current.onInterim?.(interim.trim());
      };

      rec.onerror = (e: any) => {
        const code = e?.error || '';
        // Live is only a preview — don't surface benign or permission noise here,
        // the recorder path owns real error reporting.
        if (code === 'not-allowed' || code === 'service-not-allowed') setLiveActive(false);
      };
      rec.onend = () => { setLiveActive(false); recognitionRef.current = null; };

      recognitionRef.current = rec;
      rec.start();
      setLiveActive(true);
    } catch {
      recognitionRef.current = null;
      setLiveActive(false);
    }
  }, [language]);

  // ── Start: recorder (authoritative) + live preview (cosmetic) ─────────────
  const start = useCallback(async () => {
    if (status === 'listening' || status === 'starting') return;
    cancelledRef.current = false;
    liveTextRef.current = '';
    setLastError(null);
    setEngine(null);
    cbRef.current.onInterim?.('');

    if (!canRecord()) {
      // No recorder: fall back to live-only so the feature still works.
      if (!getSpeechRecognition()) { fail('VOICE_UNSUPPORTED'); return; }
      setStatus('listening');
      startTicker();
      attachLive();
      return;
    }

    setStatus('starting');
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch (err: any) {
      // Distinguish the common cases so the UI can say something useful.
      const name = err?.name || '';
      fail(
        name === 'NotAllowedError' || name === 'SecurityError' ? 'MIC_DENIED'
          : name === 'NotFoundError' ? 'MIC_NOT_FOUND'
            : err instanceof Error ? err.message : 'MIC_ERROR',
      );
      return;
    }

    try {
      const mimeType = pickMimeType();
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorderRef.current = rec;
      chunksRef.current = [];

      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };

      rec.onstop = async () => {
        cleanupTimers();
        stopMeter();
        stream.getTracks().forEach((t) => t.stop());
        recorderRef.current = null;
        setSeconds(0);

        const elapsed = Date.now() - startedAtRef.current;
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        chunksRef.current = [];

        if (cancelledRef.current) { setStatus('idle'); return; }
        if (elapsed < MIN_MS || blob.size === 0) {
          setStatus('idle');
          if (!liveTextRef.current) fail('TOO_SHORT');
          return;
        }

        // Whisper pass — always runs, and its result wins.
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        setStatus('transcribing');
        try {
          const text = await transcribeAudio(blob, language, ctrl.signal);
          if (ctrl.signal.aborted || cancelledRef.current) { setStatus('idle'); return; }
          if (text) {
            setEngine('whisper');
            cbRef.current.onResult(text, 'whisper'); // replaces the live text
            setStatus('idle');
          } else if (liveTextRef.current) {
            setStatus('idle'); // keep whatever live captured
          } else {
            fail('NO_SPEECH');
          }
        } catch (err: any) {
          if (err?.name === 'AbortError' || ctrl.signal.aborted) { setStatus('idle'); return; }
          // Live text (if any) stays in the box — report why the upgrade failed.
          fail(err instanceof Error ? err.message : 'TRANSCRIBE_FAILED');
        } finally {
          abortRef.current = null;
        }
      };

      rec.start();
      setStatus('listening');
      startTicker();
      startMeter(stream);
      attachLive(); // parallel live preview
    } catch (err) {
      stream.getTracks().forEach((t) => t.stop());
      fail(err instanceof Error ? err.message : 'RECORDER_ERROR');
    }
  }, [status, language, attachLive]);

  const stop = useCallback(() => {
    const sr = recognitionRef.current;
    if (sr) { try { sr.stop(); } catch { /* ignore */ } }
    const mr = recorderRef.current;
    if (mr && mr.state !== 'inactive') {
      mr.stop(); // onstop runs the Whisper pass
    } else {
      cleanupTimers();
      stopMeter();
      setStatus((s) => (s === 'transcribing' ? s : 'idle'));
      setSeconds(0);
    }
    cbRef.current.onInterim?.('');
  }, []);
  stopRef.current = stop;

  /** Abandon everything: recording, live preview, and any in-flight upload. */
  const cancel = useCallback(() => {
    cancelledRef.current = true;
    cbRef.current.onInterim?.('');
    const sr = recognitionRef.current;
    if (sr) { try { sr.abort ? sr.abort() : sr.stop(); } catch { /* ignore */ } }
    abortRef.current?.abort();
    abortRef.current = null;
    const mr = recorderRef.current;
    if (mr && mr.state !== 'inactive') mr.stop();
    cleanupTimers();
    stopMeter();
    setStatus('idle');
    setSeconds(0);
    setLastError(null);
  }, []);

  const toggle = useCallback(() => {
    (status === 'listening' || status === 'starting') ? stop() : void start();
  }, [status, start, stop]);

  useEffect(() => () => {
    cancelledRef.current = true;
    cleanupTimers();
    stopMeter();
    abortRef.current?.abort();
    const sr = recognitionRef.current;
    if (sr) { try { sr.abort ? sr.abort() : sr.stop(); } catch { /* ignore */ } }
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') rec.stop();
    rec?.stream.getTracks().forEach((t) => t.stop());
  }, []);

  return {
    status,
    recording: status === 'listening' || status === 'starting',
    transcribing: status === 'transcribing',
    seconds,
    level,
    engine,
    liveActive,
    lastError,
    start, stop, cancel, toggle,
    supported: isVoiceSupported(),
  };
};
